import axios from "axios";
import cloneDeep from "lodash.clonedeep";
import startCase from "lodash.startcase";
import { defineAsyncComponent } from "vue";
import Waitlist from "./Components/waitlist.vue";
import RegistrationForm from "./Components/registration-form.vue";
import Throttle from "./Components/throttle.vue";
import AccessCode from "./Components/access-code.vue";
import ModalRemindMe from "./Components/modal-remind-me.vue";
import {ShareNetwork} from "vue-social-sharing";
import {
    PhArrowDown,
    PhArrowLeft,
    PhArrowRight,
    PhArrowUpRight,
    PhBellRinging,
    PhCalendarDots,
    PhCalendarPlus,
    PhCalendarStar,
    PhCaretDown,
    PhCheck,
    PhCircleNotch,
    PhClock,
    PhEnvelope,
    PhEnvelopeSimple,
    PhFacebookLogo,
    PhHourglass,
    PhIdentificationCard,
    PhKey,
    PhLink,
    PhLock,
    PhMapPin,
    PhShareNetwork,
    PhShoppingBag,
    PhTicket,
    PhWarningCircle,
    PhWhatsappLogo,
    PhXLogo,
} from "@phosphor-icons/vue";

import {AxiosRedirectIntercepted} from "../Shared/errors";
import {trackAddToCart, trackBeginCheckout, trackIdle, trackShare, trackViewProduct} from "../Shared/analytics";
import {initCopyShareButtons} from "../Shared/clipboardShare";
import {chargeCalculationMethod} from "../Shared/Enums/ChargeCalculationMethod";
import {chargeType} from "../Shared/Enums/ChargeType";
import {injectHideyShowyHack} from "./hideyShowyHack";
import {createApp, currencyFormat} from "../Shared/common";
import { CURRENCY_FORMAT_OPTIONS } from "../Shared/VueComponents/numberFormat";
import {Big} from "../Shared/number";

const RESERVATION_FAILED_STATUS = 1;

const SubmissionAction = Object.freeze({
    AddToCart: "AddToCart",
    Checkout: "Checkout",
});

const hasCartExpired = () => {
    var field = "expired";
    var url = window.location.href;
    if (url.indexOf("?" + field + "=") != -1) return true;
    else if (url.indexOf("&" + field + "=") != -1) return true;
    return false;
};

const hasCartSeatFulfillmentFailed = () => {
    var field = "seatFulfillmentFailed";
    var url = window.location.href;
    if (url.indexOf("?" + field + "=") != -1) return true;
    else if (url.indexOf("&" + field + "=") != -1) return true;
    return false;
};

const addCurrentQueryStringToLinks = (element) => {
    const links = element.querySelectorAll("a");
    links.forEach((link) => {
        const originalHref = link.getAttribute("href");

        const isHrefIsAFragment = (originalHref || "").indexOf("#") === 0;
        if (isHrefIsAFragment) return;

        const currentQuerystring = window.location.search.substring(1);
        if (!currentQuerystring || currentQuerystring === "") return;

        const originalHrefHasQuerystring = originalHref.indexOf("?") !== -1;
        const separator = originalHrefHasQuerystring ? "&" : "?";

        const newHref = originalHref + separator + currentQuerystring;
        link.setAttribute("href", newHref);
    });
};

const preLoadeddata = cloneDeep(window.preLoadeddata);

// Resolved UI variant — set on <body data-ui-variant="..."> by _Layout.cshtml.
// v2 component code is loaded only when v2 is the active variant; v1 visitors
// never download these chunks.
const isV2 = document.body.dataset.uiVariant === 'v2';

const v2Components = isV2 ? {
    "event-v2-stepper": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "./Components/EventV2/Stepper.vue")),
    "event-v2-access-code": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "./Components/EventV2/AccessCode.vue")),
    "event-v2-remind-me-modal": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "./Components/EventV2/RemindMeModalV2.vue")),
    "event-v2-waitlist": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "./Components/EventV2/WaitlistV2.vue")),
    "event-v2-throttle": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "./Components/EventV2/ThrottleV2.vue")),
    "number-flow-v2": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "../Shared/VueComponents/NumberFlowV2.vue")),
    "add-to-calendar-dialog-v2": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "../Shared/VueComponents/AddToCalendarDialogV2.vue")),
    "pre-registration-form-v2": defineAsyncComponent(() =>
        import(/* webpackChunkName: "event-v2" */ "./Components/EventV2/PreRegistrationFormV2.vue")),
} : {};

const vm = createApp({
    data: () =>
        Object.assign(preLoadeddata, {
            currencyFormatOptions: CURRENCY_FORMAT_OPTIONS,
            pageIndex: 1,
            showModal: false,
            modalTitle: "",
            modalType: "info",
            modalMessage: "",
            busy: false,
            busyAction: null,
            showAddedConfirmation: false,
            canPurchase: true,
            showNoTicketsSelectedAlert: false,
            showMustPurchaseAdditionalTicketsAlert: false,
            showMustPurchaseAdditionalTicketsAlertMessage: "",
            showExpiredAlert: hasCartExpired(),
            showSeatFulfillmentFailedAlert: hasCartSeatFulfillmentFailed(),
            showAddToCalendar: false,
            showRemindMeModal: false,
            autoScanEnabled: preLoadeddata.autoScanPreference,
            chart: null,
            selectedSeats: [],
            itemCartView: [],
            showSeatSelectionCart: false,
            showTicketDialog: false,
            selectedFloor: "",
            holdToken: "",
            bestAvaliableSectionsSelected: {},
            seatsSelectionErrors: [],
            seatSelectionTicketTypeDialogData: {},
            seatSelectionConfirmSelectionCallback: null,
            allSeatIds: {}, // This is currently only being populated when we have a seated table event. This is filled when a section has 'table' in the name
            seatBundles: {}, // This will only be filled with items if a ticketType has the word 'whole' in the name.
            v2CtaIsFloating: false,
            v2CtaShouldStick: false,
        }),
    components: {
        Waitlist,
        Throttle,
        AccessCode,
        RegistrationForm,
        ModalRemindMe,
        ShareNetwork,
        ...v2Components,
    },
    mounted() {
        if (this.seatingSettings && this.seatingSettings.seatingChartKey) {
            this.loadSeatsIOScript();
        }
        if (this.autoPrintPreference && this.isDoorSales && this.lastOrderId && this.lastCartStatus === 3) {
            const orderUrl = `${this.ordersBaseUrl}/orders/${this.lastOrderId}/wait-for-order?autoPrint=true`;
            window.open(orderUrl, "_blank", "");
        }

        // Simulating an equivalent of document.ready (to be used in bootstrapping google tag manager to subscribe to events etc)
        this.$nextTick(function () {
            const applicationReadyEvent = new CustomEvent("application-ready", {
                detail: {when: Date()},
            });

            if (this.hideyShowyHackEnabled === true) {
                injectHideyShowyHack(this);
            }

            document.body.dispatchEvent(applicationReadyEvent);
            trackViewProduct({id: this.eventId, name: this.eventName});

            if (this.$uiVariant === "v2") {
                this.attachV2InteractionHandlers();
            }
        });
        setInterval(trackIdle, 60 * 1000);
    },
    beforeUnmount() {
        if (this._v2OnScroll) {
            window.removeEventListener("scroll", this._v2OnScroll);
            window.removeEventListener("resize", this._v2OnScroll);
            this._v2OnScroll = null;
        }
        if (this._v2OnAnchorClick) {
            const root = this._v2AnchorRoot || document;
            root.removeEventListener("click", this._v2OnAnchorClick);
            this._v2OnAnchorClick = null;
            this._v2AnchorRoot = null;
        }
        if (this._v2DetailsObserver) {
            this._v2DetailsObserver.disconnect();
            this._v2DetailsObserver = null;
        }
    },
    watch: {
        // clear an alerts whenever there are changes
        // to requested inventory
        requestedInventory: {
            handler() {
                this.showNoTicketsSelectedAlert = false;
                this.showMustPurchaseAdditionalTicketsAlert = false;
                (this.showMustPurchaseAdditionalTicketsAlertMessage = ""), (this.showExpiredAlert = false);
                this.accessCode.showWarning = false;

                // v2 dependent-ticket reactive zeroing: when a prerequisite ticket is
                // decremented back to zero, any dependent ticket counts get cleared so
                // the user never sees an orphan that would fail submit-time validation.
                // Submit-time validation in ticketsRequiringOtherInventoriesToBeSelected()
                // still acts as a backstop for any code path that bypasses this watcher.
                if (this.$uiVariant === "v2") {
                    Object.keys(this.inventory).forEach((id) => {
                        if (this.requestedInventory[id] > 0 && this.isLocked(id)) {
                            this.requestedInventory[id] = 0;
                        }
                    });
                }

                const requestedInventoryChangedEvent = new CustomEvent("requested-inventory-changed", {detail: {when: Date(), vm: this}});
                document.body.dispatchEvent(requestedInventoryChangedEvent);
            },
            deep: true,
        },
        autoScanPreference: {
            handler() {},
            deep: true,
        },
    },
    computed: {
        numberOfTicketsSelected() {
            const itemsNotBundled = this.selectedSeats.filter((x) => !x.isBundle);
            const unBundleItemCount = Object.values(itemsNotBundled).reduce((total, item) => {
                return total + item.count;
            }, 0);

            return unBundleItemCount + Object.keys(this.seatBundles).length;
        },
        showTotal() {
            return this.anyTicketsSelected;
        },
        reserveSeatsSelected() {
            const totalValues = this.selectedSeats;
            const itemsNotBundled = totalValues.filter((x) => !x.isBundle);
            return [...itemsNotBundled, ...Object.values(this.seatBundles)];
        },
        selectedTickets() {
            return Object.keys(this.requestedInventory)
                .filter((inventoryId) => this.requestedInventory[inventoryId] > 0)
                .map((inventoryId) => {
                    const quantity = this.requestedInventory[inventoryId];
                    const inventoryItem = this.inventory[inventoryId];
                    const priceBase = Big(inventoryItem.price).minus(inventoryItem.bookingFee);

                    return {
                        inventoryId,
                        quantity,
                        name: inventoryItem.name,
                        price: inventoryItem.price,
                        total: inventoryItem.price * quantity,
                        bookingFee: inventoryItem.bookingFee,
                        totalBookingFee: inventoryItem.bookingFee * quantity,
                        requiresAnyOfTheseInventoriesToBeSelected: inventoryItem.requiresAnyOfTheseInventoriesToBeSelected,
                        priceBase: priceBase.toNumber(),
                    };
                });
        },

        requestedInventoryToPost() {
            return this.selectedTickets.reduce((obj, ticket) => {
                obj[ticket.inventoryId] = ticket.quantity;
                return obj;
            }, {});
        },

        anyTicketsSelected() {
            return this.selectedTickets.length;
        },

        total() {
            return this.selectedTickets.map((ticket) => ticket.total).reduce((total, current) => total + current, 0);
        },

        totalBookingFees() {
            return this.selectedTickets
                .map((ticket) => ticket.totalBookingFee)
                .reduce((totalBookingFee, current) => totalBookingFee + current, 0);
        },

        showNextButton() {
            return this.pageIndex < this.totalNumberOfPages;
        },

        showPreviousButton() {
            return this.totalNumberOfPages > 1 && this.showGetTicketsButton;
        },

        showGetTicketsButton() {
            return !this.showNextButton;
        },

        showTransactionFeeMessage() {
            return this.charges && this.charges.length > 0;
        },

        totalTransactionFeeAmount() {
            if (!this.charges || this.charges.length === 0) return 0;

            return this.charges.reduce((totalFee, charge, index) => {
                const baseAmount =
                    charge.calculationMethod === chargeCalculationMethod.highestTicketPrice ? this.getHighestTicketPriceBase() : this.total;

                const baseAmountBig = Big(baseAmount);
                const applicableBracket = charge.chargeBrackets
                    ?.sort((a, b) => b.minimumAmount - a.minimumAmount)
                    ?.find((bracket) => baseAmountBig.gte(bracket.minimumAmount));

                if (!applicableBracket) {
                    return totalFee;
                }

                const chargeAmount =
                    charge.chargeType === chargeType.percentage
                        ? baseAmount <= 0
                            ? 0
                            : Big(applicableBracket.amount).div(100).times(baseAmount).toNumber()
                        : applicableBracket.amount;

                return totalFee + chargeAmount;
            }, 0);
        },
        showSeatSelection() {
            return this.showSeatSelectionCart;
        },
        showPicker() {
            return this.showTicketDialog;
        },
        totalTransactionFeeMessage() {
            if (this.showTransactionFeeMessage) {
                if (this.anyTicketsSelected) {
                    const feeAmount = this.totalTransactionFeeAmount;
                    if (feeAmount > 0) {
                        return `a transaction fee of $${feeAmount.toFixed(2)}`;
                    }
                } else if (this.charges && this.charges.length > 0) {
                    const lowestPossibleAmount = this.getLowestPossibleTransactionFee();
                    if (lowestPossibleAmount > 0) {
                        return `a transaction fee from $${lowestPossibleAmount.toFixed(2)}`;
                    }
                }
            }
            return "";
        },
        disableUIInteraction() {
            if (this.seatingSettings != null) {
                return this.busy || !this.canPurchase;
            }

            return this.busy;
        },

        isAddToCartBusy() {
            return this.busyAction === SubmissionAction.AddToCart;
        },

        isCheckoutBusy() {
            return this.busyAction === SubmissionAction.Checkout;
        },

        isCollectionMode() {
            return this.multiEventPurchasingEnabled && this.collectionId && !this.isSeatedEvent;
        },

        cartPageUrl() {
            if (!this.collectionId) return null;
            const params = new URLSearchParams(window.location.search);
            params.delete("redirect");
            const cleanSearch = params.toString();
            const pathname = window.location.pathname.replace(/^\/+/, "/");
            const redirect = encodeURIComponent(pathname + (cleanSearch ? `?${cleanSearch}` : ""));
            return `/collection/cart/?id=${this.collectionId}&redirect=${redirect}`;
        },
    },
    methods: {
        // v2 page interactions: sticky-CTA gating + floating shadow when the ticket
        // card extends past the viewport bottom, and smooth scroll for in-page
        // anchor links (e.g. the "Event details ↗" link in the left card).
        attachV2InteractionHandlers() {
            // Copy-link buttons in the v2 share rows. Idempotent — safe to
            // call from mounted() since each button keys its listener off
            // a one-shot `data-share-copy` attribute that survives but only
            // gets a handler attached once per mount.
            initCopyShareButtons(document.getElementById("events-page"));

            const ticketCard = this.$refs.ticketForm ? this.$refs.ticketForm.parentElement : null;
            if (ticketCard) {
                const update = () => {
                    const rect = ticketCard.getBoundingClientRect();
                    const vh = window.innerHeight;
                    this.v2CtaIsFloating = rect.bottom > vh;

                    // Sticky gate: CTA sticks at viewport bottom only while doing so
                    // wouldn't cover the first ticket card. Once the user scrolls up
                    // enough that the first ticket's bottom would sit below where the
                    // CTA's top edge would land, sticky disengages and the CTA falls
                    // back to its natural position at the bottom of the card (which is
                    // below the viewport from that scroll position, so it disappears).
                    // Mirrors the prototype's `cardTopBuffer` check.
                    const ctaEl = ticketCard.querySelector(".event-v2-cta-footer");
                    const ctaHeight = ctaEl ? ctaEl.offsetHeight : 0;
                    const firstTicket = ticketCard.querySelector('[data-testid="ticket-block"]');
                    const firstTicketBottom = firstTicket ? firstTicket.getBoundingClientRect().bottom : 0;
                    this.v2CtaShouldStick =
                        rect.bottom > vh &&
                        (!firstTicket || firstTicketBottom <= vh - ctaHeight);
                };
                window.addEventListener("scroll", update, {passive: true});
                window.addEventListener("resize", update);
                update();
                this._v2OnScroll = update;
            }

            // Scoped to the v2 page root so smooth-scroll only intercepts hash
            // anchors inside the event page, not unrelated anchors elsewhere in
            // the document (e.g. modals, share widgets).
            const anchorRoot = document.getElementById("events-page");
            if (anchorRoot) {
                const onAnchorClick = (event) => {
                    const link = event.target.closest && event.target.closest('a[href^="#"]');
                    if (!link) return;
                    const hash = link.getAttribute("href");
                    if (!hash || hash === "#") return;
                    const target = document.querySelector(hash);
                    if (!target) return;
                    event.preventDefault();
                    target.scrollIntoView({behavior: "smooth", block: "start"});
                };
                anchorRoot.addEventListener("click", onAnchorClick);
                this._v2OnAnchorClick = onAnchorClick;
                this._v2AnchorRoot = anchorRoot;
            }

            // Hide the description jump button while its section is on screen.
            const detailsLink = document.querySelector("[data-event-details-link]");
            const detailsSection = document.getElementById("event-description");
            if (detailsLink && detailsSection && "IntersectionObserver" in window) {
                const observer = new IntersectionObserver(
                    (entries) => {
                        for (const entry of entries) {
                            detailsLink.style.display = entry.isIntersecting ? "none" : "";
                        }
                    },
                    {threshold: 0},
                );
                observer.observe(detailsSection);
                this._v2DetailsObserver = observer;
            }
        },
        loadSeatsIOScript() {
            const script = document.createElement("script");
            script.src = "https://cdn-oc.seatsio.net/chart.js";
            script.async = true;

            script.addEventListener("load", () => {
                this.loadSeatPicker();
            });

            document.body.appendChild(script);
            window.addEventListener(
                "message",
                (object) => {
                    if (object.origin != "https://cdn-oc.seatsio.net") return;
                    try {
                        var data = JSON.parse(object.data);
                        if (data.messageType == "updateSeats") {
                            if (data.label.toLowerCase().includes("table")) {
                                if (!this.allSeatIds[data.parent]) {
                                    this.allSeatIds[data.parent] = [data.label];
                                } else {
                                    this.allSeatIds[data.parent].push(data.label);
                                }
                            }
                        }
                    } catch (e) {
                        return false;
                    }
                },
                false
            );
        },
        showHideSeatSelect() {
            this.showSeatSelectionCart = !this.showSeatSelectionCart;
        },
        getValidators() {
            var validators = [];

            if (this.seatingSettings.enforceConsecutiveSeats) {
                validators.push({
                    type: "consecutiveSeats",
                });
            }

            if (this.seatingSettings.enforceNoOrphanedSeats) {
                validators.push({
                    type: "noOrphanSeats",
                });
            }

            return validators;
        },
        loadSeatPicker() {
            if (!document.getElementById("seatsPicker")) {
                return;
            }
            const tickets = this.seatingSettings.seatedCategories.map((x) => {
                return {
                    category: x.ticketPoolId,
                    ticketTypes: x.ticketTypes,
                };
            });

            const ticketTypes = tickets.map((x) => {
                return x.ticketTypes;
            });

            const ticketTypesFlattened = ticketTypes.concat.apply([], ticketTypes);

            const maximumSelectedItems = ticketTypesFlattened.map((ticket) => {
                return {
                    ticketType: ticket.ticketType,
                    quantity: this.inventory[ticket.ticketType].maximumQuantity,
                };
            });

            this.chart = new window.seatsio.SeatingChart({
                session: "start",
                divId: "seatsPicker",
                workspaceKey: this.seatingSettings.workspaceKey,
                event: this.eventId,
                pricing: tickets,
                priceFormatter: function (price) {
                    return "$" + price;
                },
                maxSelectedObjects: maximumSelectedItems,
                onObjectSelected: this.reserveSeat,
                onObjectDeselected: this.unReserveSeat,
                onFloorChanged: (item) => {
                    this.selectedFloor = item ? item.name : "";
                },
                onTicketTypePrompt: this.showTicketTypesDialog,
                channels: ["NO_CHANNEL"],
                multiSelectEnabled: this.seatingSettings.useBestAvaliable ? false : this.seatingSettings.multiSelectEnabled,
                showFullScreenButton: this.seatingSettings.useBestAvaliable ? false : true,
                selectionValidators: this.getValidators(),
                onSelectionInvalid: (errors) => {
                    this.canPurchase = false;
                    this.seatsSelectionErrors = errors.map((x) => {
                        return {
                            type: "SeatsPickerError",
                            text: this.getSelectionErrorMessage(x),
                        };
                    });
                },
                onSelectionValid: () => {
                    this.canPurchase = true;
                    this.seatsSelectionErrors = [];
                },
                onHoldTokenExpired: () => {
                    this.canPurchase = false;
                },
                onSessionInitialized: () => {
                    this.seatsSelectionErrors = [];
                    this.canPurchase = true;
                },
                showFullScreenButton: false,
                availableCategories: this.getAvaliableCategoriesOnMount(),
                showSectionContents: this.seatingSettings.showSectionContents,
                isObjectVisible: (object, extraConfig) => {
                    if (object.type === "seat") {
                        window.parent.postMessage(
                            `{"label": "${object.label}","messageType":"updateSeats", "parent": "${object.labels.parent}"}`,
                            extraConfig.parentUrl
                        );
                    }

                    return true;
                },
                extraConfig: {
                    parentUrl: window.location.origin,
                },
            }).render();
        },
        getAvaliableCategoriesOnMount() {
            return this.seatingSettings.useBestAvaliable ? ["none"] : this.seatingSettings.seatedCategories.map((x) => x.ticketPoolId);
        },
        getOwnParentDisplayName(section, objectType) {
            if (objectType === "GeneralAdmissionArea") return "";

            if (section.toLowerCase().includes("table")) return "Table";

            return "Row";
        },
        showTicketTypesDialog(params, confirmSelection) {
            this.seatSelectionConfirmSelectionCallback = confirmSelection;

            const section = startCase(params.objectToSelect.labels.section);
            const ownType = startCase(params.objectToSelect.objectType);
            const parentType = this.getOwnParentDisplayName(section, params.objectToSelect.objectType);
            const parentNumber = params.objectToSelect.labels.parent;
            const ownNumber = params.objectToSelect.labels.own;

            if (this.isSeatObjectATable(section)) {
                const seatsWithParent = this.allSeatIds[parentNumber];

                const seatInfos = seatsWithParent.map((seatId) => {
                    return this.chart.findObject(seatId);
                });

                Promise.all(seatInfos).then((objectInfos) => {
                    var availableTicketTypes = params.ticketTypes.map((x) => {
                        return {
                            Name: this.inventory[x.ticketType].name,
                            price: this.currencyFormat(this.inventory[x.ticketType].price),
                            ticketTypeId: x.ticketType,
                            bookingType: this.isTicketTypeABundle(x.ticketType) ? "bundle" : "single",
                            parentId: parentNumber,
                        };
                    });

                    const hasBookedSeats = objectInfos.filter((x) => x.status !== "free").length > 0;

                    if (hasBookedSeats) {
                        availableTicketTypes = availableTicketTypes.filter((x) => x.bookingType != "bundle");
                    }

                    this.seatSelectionTicketTypeDialogData = {
                        ticketTypes: availableTicketTypes,
                        sectionName: section,
                        row: `${parentType} ${parentNumber} ${ownType} ${ownNumber}`,
                        seatObject: params.objectToSelect,
                    };

                    this.showTicketDialog = true;
                });
            } else {
                this.seatSelectionTicketTypeDialogData = {
                    ticketTypes: params.ticketTypes.map((x) => {
                        return {
                            Name: this.inventory[x.ticketType].name,
                            price: this.currencyFormat(this.inventory[x.ticketType].price),
                            ticketTypeId: x.ticketType,
                            bookingType: this.isTicketTypeABundle(x.ticketType) ? "bundle" : "single",
                            parentId: parentNumber,
                        };
                    }),
                    sectionName: section,
                    row: `${parentType} ${parentNumber} ${ownType} ${ownNumber}`,
                    seatObject: params.objectToSelect,
                };

                this.showTicketDialog = true;
            }
        },
        selectSeatFromDialog(ticketTypeId, bookingType, parentNumber) {
            const selectionError = [
                {
                    type: "SeatsPickerError",
                    text: "Your selected seats cannot be reserved - adjust selection and try again.",
                },
            ];

            if (bookingType === "bundle") {
                const seatsWithParent = this.allSeatIds[parentNumber];
                this.chart
                    .doSelectObjects(
                        seatsWithParent.map((x) => {
                            return {
                                id: x,
                                ticketType: ticketTypeId,
                            };
                        })
                    )
                    .catch(() => {
                        this.seatsSelectionErrors = selectionError;
                    });
            } else {
                this.seatSelectionConfirmSelectionCallback(ticketTypeId).catch((e) => {
                    this.seatsSelectionErrors = selectionError;
                });
            }

            this.showTicketDialog = false;
        },
        cancelSelectSeatFromDialog() {
            this.showTicketDialog = false;
        },
        quantities(ticketId) {
            const size =
                this.inventory[ticketId].maximumQuantity < this.inventory[ticketId].minimumQuantity
                    ? this.inventory[ticketId].maximumQuantity
                    : this.inventory[ticketId].maximumQuantity - this.inventory[ticketId].minimumQuantity + 1;

            return [...Array(size)].map((_, i) => ({
                key: `${ticketId}-${i}`,
                value: i + this.inventory[ticketId].minimumQuantity,
            }));
        },
        updateCategoriesOnSeatingChart(categories) {
            this.chart.changeConfig({
                availableCategories: categories, // this will deselect objects that belong to an unavailable category
            });
        },
        ticketSelectonOnChangeEvent(event, ticketId) {
            const ticketCount = event.target.value;
            if (this.seatingSettings && this.seatingSettings.useBestAvaliable) {
                const categoryidfilter = this.seatingSettings.seatedCategories.filter(
                    (x) => x.ticketTypes.filter((y) => y.ticketType === ticketId).length > 0
                );
                const categoryKey = categoryidfilter[0].ticketPoolId;

                if (!this.bestAvaliableSectionsSelected[categoryKey] && ticketCount > 0) {
                    this.bestAvaliableSectionsSelected[categoryKey] = ticketCount;
                    this.updateCategoriesOnSeatingChart(Object.keys(this.bestAvaliableSectionsSelected));
                    return;
                }

                if (ticketCount == 0) {
                    delete this.bestAvaliableSectionsSelected[categoryKey];
                    if (Object.keys(this.bestAvaliableSectionsSelected).length === 0) {
                        this.updateCategoriesOnSeatingChart(["none"]);
                    } else {
                        this.updateCategoriesOnSeatingChart(Object.keys(this.bestAvaliableSectionsSelected));
                    }
                }
            }
        },
        moveToNextPage() {
            this.pageIndex = this.pageIndex + 1;
            const transitionInMs = 500;
            setTimeout(() => {
                window.scrollTo(0, 0);
            }, transitionInMs);
        },

        moveToPreviousPage() {
            this.pageIndex = this.pageIndex - 1;
            this.showNoTicketsSelectedAlert = false;
            this.showMustPurchaseAdditionalTicketsAlert = false;
        },

        onSocialShareOpen(network, url) {
            trackShare(network, url);
        },

        showNoTicketsSelectedAlertAndSetFocus() {
            this.showNoTicketsSelectedAlert = true;
            this.showExpiredAlert = false;
            this.$nextTick(() => this.$refs.noTicketsSelectedAlert.focus());
        },

        showMustPurchaseAdditionalTicketsAlertAndFocus(message) {
            this.showMustPurchaseAdditionalTicketsAlert = true;
            this.showMustPurchaseAdditionalTicketsAlertMessage = message;
            this.showExpiredAlert = false;
            this.$nextTick(() => this.$refs.mustPurchaseAdditionalTicketsAlert.focus());
        },
        hasSeatAlreadyBeenAddedApartOfABundle(sectionId, parentId, ticketType) {
            return this.seatBundles[this.generateSeatBundleId(sectionId, parentId, ticketType)];
        },
        isTicketTypeABundle(ticketType) {
            return this.inventory[ticketType].name.toLowerCase().includes("table of");
        },
        generateSeatBundleId(sectionId, parentId, ticketType) {
            return `${sectionId}|${parentId}|${ticketType}`;
        },
        isSeatObjectATable(sectionName) {
            return sectionName.toLowerCase().includes("table");
        },
        reserveSeat(seatInfo, selectedItem) {
            const ticketName = this.inventory[selectedItem.ticketType].name;
            const seatParent = seatInfo.labels.parent ?? "";
            const isBundle = this.isTicketTypeABundle(selectedItem.ticketType);
            const section = seatInfo.objectType === "GeneralAdmissionArea" ? seatInfo.label : seatInfo.labels.section ?? "";
            const seatNumber = seatInfo.objectType === "GeneralAdmissionArea" ? "0" : seatInfo.labels.own;
            if (isBundle) {
                if (!this.hasSeatAlreadyBeenAddedApartOfABundle(section, seatParent, selectedItem.ticketType)) {
                    this.seatBundles[this.generateSeatBundleId(section, seatParent, seatInfo.selectedTicketType)] = {
                        section: section,
                        rowNumber: seatParent,
                        price: this.inventory[selectedItem.ticketType].price,
                        parentType: this.getOwnParentDisplayName(section, seatInfo.objectType),
                        isBundle,
                        count: 1,
                    };
                    this.addTicket(selectedItem.ticketType);
                }
            } else {
                this.addTicket(selectedItem.ticketType);
            }
            this.selectedSeats.push({
                id: seatInfo.label,
                name: ticketName,
                price: this.inventory[selectedItem.ticketType].price,
                floor: this.selectedFloor ?? "",
                section: section,
                seatNumber: seatNumber,
                rowNumber: seatParent,
                type: seatInfo.objectType,
                count: 1,
                ticketPoolId: seatInfo.category.key,
                ticketTypeId: seatInfo.selectedTicketType,
                isBundle,
                bundleId: this.generateSeatBundleId(section, seatParent, seatInfo.selectedTicketType),
                parentType: this.getOwnParentDisplayName(section, seatInfo.objectType),
            });
        },
        unReserveSeat(seatInfo, selectedItem) {
            const seatParent = seatInfo.labels.parent ?? "";
            const section = seatInfo.objectType === "GeneralAdmissionArea" ? seatInfo.label : seatInfo.labels.section ?? "";

            if (
                this.isTicketTypeABundle(selectedItem.ticketType) &&
                this.hasSeatAlreadyBeenAddedApartOfABundle(section, seatParent, selectedItem.ticketType)
            ) {
                delete this.seatBundles[this.generateSeatBundleId(section, seatParent, selectedItem.ticketType)];
                const adjacentSeats = this.allSeatIds[seatParent];
                this.chart.deselectObjects(adjacentSeats);
            }

            this.removeTicket(selectedItem.ticketType);
            var index = this.selectedSeats.findIndex((x) => x.id === seatInfo.label && x.ticketTypeId === selectedItem.ticketType);
            if (index > -1) {
                this.selectedSeats.splice(index, 1);
            }
        },
        addTicket(inventoryId) {
            const {minimumQuantity, maximumQuantity} = this.inventory[inventoryId];
            const currentNumberOfTickets = this.requestedInventory[inventoryId];
            if (this.requestedInventory[inventoryId] < maximumQuantity) {
                const numberOfTicketsToAdd = currentNumberOfTickets == 0 ? minimumQuantity : 1;
                this.requestedInventory[inventoryId] += numberOfTicketsToAdd;
            }
        },
        removeTicket(inventoryId) {
            const {minimumQuantity, maximumQuantity} = this.inventory[inventoryId];
            const currentNumberOfTickets = this.requestedInventory[inventoryId];
            if (this.requestedInventory[inventoryId] != 0) {
                const numberOfTicketsToRemove = currentNumberOfTickets == minimumQuantity ? minimumQuantity : 1;
                this.requestedInventory[inventoryId] -= numberOfTicketsToRemove;
            }
        },
        isRemoveDisabled(inventoryId) {
            return this.requestedInventory[inventoryId] == 0;
        },

        isAddDisabled(inventoryId) {
            return this.requestedInventory[inventoryId] == this.inventory[inventoryId].maximumQuantity;
        },

        // v2 ticket locking — a ticket is "locked" while it depends on at least one
        // other inventory item that has not yet been selected. The submit-time guard
        // in ticketsRequiringOtherInventoriesToBeSelected() catches orphans server-side;
        // this lets the UI reflect the same constraint inline so the stepper, card
        // styling, and "Requires …" label can react to prerequisite changes.
        isLocked(inventoryId) {
            const item = this.inventory[inventoryId];
            if (!item) return false;
            const requires = item.requiresAnyOfTheseInventoriesToBeSelected;
            if (!requires || requires.length === 0) return false;
            return !requires.some((reqId) => this.requestedInventory[reqId] > 0);
        },

        lockedRequirementLabel(inventoryId) {
            const item = this.inventory[inventoryId];
            if (!item) return "";
            const requires = item.requiresAnyOfTheseInventoriesToBeSelected;
            if (!requires || requires.length === 0) return "";
            const names = requires.map((reqId) => this.inventory[reqId]?.name).filter(Boolean);
            if (names.length === 0) return "";
            if (names.length === 1) return `Requires ${names[0]}`;
            const head = names.slice(0, -1).join(", ");
            return `Requires ${head} or ${names[names.length - 1]}`;
        },

        activeClass(inventoryId) {
            return this.requestedInventory[inventoryId] > 0 ? "active" : "";
        },

        showOnCurrentPage(inventoryId) {
            return this.inventory[inventoryId].pageToShowOn == this.pageIndex;
        },

        // Safari lacks CSS interpolate-size, so set --content-height for the height fallback.
        setDisclosureHeight(event) {
            const content = event.currentTarget.nextElementSibling;
            if (content) {
                content.style.setProperty("--content-height", `${content.scrollHeight}px`);
            }
        },

        groupSelectedCount(inventoryIds) {
            return inventoryIds.reduce((sum, id) => sum + (this.requestedInventory[id] || 0), 0);
        },

        // A group can sit entirely off the current page, so gate its header,
        // accordion, and separators on whether any of its tickets are visible.
        groupHasOnCurrentPage(inventoryIds) {
            return inventoryIds.some((id) => this.showOnCurrentPage(id));
        },

        groupOptionsLabel(inventoryIds) {
            const count = inventoryIds.filter((id) => this.showOnCurrentPage(id)).length;
            return `${count} ${count === 1 ? "option" : "options"}`;
        },

        redirectToPage(redirectTo) {
            window.location = redirectTo;
        },
        getSelectionErrorMessage(errorType) {
            switch (errorType) {
                case "noOrphanSeats":
                    return "No orphan seats are allowed";
                case "consecutiveSeats":
                    return "You must select consecutive seats";
            }
        },
        handleReservationFailure() {
            this.showModalDialog(
                "Tickets are reserved",
                "The ticket(s) you requested are currently reserved by other customers. Try selecting a smaller number of tickets, a different ticket type or try again later."
            );
        },

        handleError(error) {
            if (error instanceof AxiosRedirectIntercepted) return;

            console.error("Something went wrong", error);

            this.showModalDialog("Something went wrong", "Oops! Something went wrong. Wanna try again?");
        },

        showModalDialog(title, message) {
            this.showModal = true;
            this.modalTitle = title;
            this.modalMessage = message;
        },

        showAddToCalendarDialog() {
            this.showAddToCalendar = true;
            document.body.className += " " + "modal-open";
        },

        closeAddToCalendarDialog() {
            window.location.reload(true);
        },

        showBusy() {
            this.busy = true;
            return Promise.resolve();
        },
        hideBusy() {
            this.busy = false;
            this.busyAction = null;
        },
        validate() {
            if (!this.anyTicketsSelected) {
                this.showNoTicketsSelectedAlertAndSetFocus();
                return false;
            }

            var invalidTickets = this.ticketsRequiringOtherInventoriesToBeSelected();
            if (invalidTickets.length) {
                const message = `${invalidTickets.join(", ")} can only be purchased with an additional ticket selection`;
                this.showMustPurchaseAdditionalTicketsAlertAndFocus(message);
                return false;
            }

            return true;
        },

        ticketsRequiringOtherInventoriesToBeSelected() {
            var selectedInventoryIds = this.selectedTickets.map((ticket) => ticket.inventoryId);

            const ticketRequiresOtherInventoriesToBeSelected = (ticket) =>
                ticket.requiresAnyOfTheseInventoriesToBeSelected.length &&
                ticket.requiresAnyOfTheseInventoriesToBeSelected.every((inventoryId) => selectedInventoryIds.indexOf(inventoryId) == -1);

            return this.selectedTickets.filter(ticketRequiresOtherInventoriesToBeSelected).map((ticket) => ticket.name);
        },

        buildAnalyticsData(cartId) {
            return {
                eventId: this.eventId,
                eventName: this.eventName,
                inventory: this.selectedTickets.map((ticket) => ({
                    id: ticket.inventoryId,
                    name: ticket.name,
                    quantity: ticket.quantity,
                    price: ticket.price,
                    priceBase: ticket.priceBase.toFixed(2),
                })),
                total: this.total,
                cartId: cartId,
                venueName: this.venueDescription,
            };
        },

        submit(event) {
            if (!this.validate()) return;

            this.showBusy()
                .then(this.postTickets(event.target.action))
                .then((response) => {
                    const {status, redirectUrl, cartId} = response.data;

                    if (status === RESERVATION_FAILED_STATUS) {
                        this.hideBusy();
                        this.handleReservationFailure();
                        return;
                    }

                    const analyticsData = this.buildAnalyticsData(cartId);
                    trackAddToCart(analyticsData);
                    trackBeginCheckout(analyticsData);
                    this.redirectToPage(redirectUrl);
                })
                .catch((error) => {
                    this.hideBusy();
                    this.handleError(error);
                });
        },

        submitWithAction(action) {
            if (!this.validate()) return;

            const form = this.$refs.ticketForm;
            this.busyAction = action;
            this.showBusy()
                .then(this.postTickets(form.action, action))
                .then((response) => {
                    const {status, redirectUrl, cartId} = response.data;

                    if (status === RESERVATION_FAILED_STATUS) {
                        this.hideBusy();
                        this.handleReservationFailure();
                        return;
                    }

                    const analyticsData = this.buildAnalyticsData(cartId);
                    trackAddToCart(analyticsData);
                    if (action === SubmissionAction.Checkout) {
                        trackBeginCheckout(analyticsData);
                    }

                    if (action === SubmissionAction.AddToCart) {
                        this.handleAddToCartSuccess();
                        this.hideBusy();
                    } else {
                        this.redirectToPage(redirectUrl);
                    }
                })
                .catch((error) => {
                    this.hideBusy();
                    this.handleError(error);
                });
        },

        handleAddToCartSuccess() {
            const addedCount = Object.values(this.requestedInventory).reduce((sum, qty) => sum + qty, 0);
            Object.keys(this.requestedInventory).forEach((id) => {
                this.requestedInventory[id] = 0;
            });
            this.collectionCartTicketCount += addedCount;
            this.showAddedConfirmation = true;
            setTimeout(() => {
                this.showAddedConfirmation = false;
            }, 2000);

            // Tell the shared Roadie cart drawer (a separate bundle) to refetch.
            // Emitted on window.EventBus so it crosses bundles in dev and prod.
            window.EventBus?.$emit("cart:updated");
        },

        postTickets(url, action = null) {
            var reservedSeats = [];
            if (this.seatingSettings) {
                this.holdToken = this.chart.holdToken;

                if (this.seatingSettings.useBestAvaliable) {
                    reservedSeats = Object.keys(this.requestedInventoryToPost).map((inventoryId) => {
                        const categoryidfilter = this.seatingSettings.seatedCategories.filter(
                            (x) => x.ticketTypes.filter((y) => y.ticketType === inventoryId).length > 0
                        );
                        const categoryKey = categoryidfilter[0].ticketPoolId;

                        return {
                            count: this.requestedInventoryToPost[inventoryId],
                            ticketPoolId: categoryKey,
                            ticketTypeId: inventoryId,
                        };
                    });
                } else {
                    reservedSeats = this.selectedSeats.map((seat) => {
                        return {
                            ...seat,
                        };
                    });
                }
            }

            return () =>
                axios.post(url, {
                    accessCodeId: this.accessCode.accessCodeId,
                    accessCode: this.accessCode.accessCode,
                    autoScanForOrder: this.autoScanEnabled,
                    requestedInventory: this.requestedInventoryToPost,
                    reservedSeats: reservedSeats,
                    partnerCustomerId: window?.Converge?.identity?.personId,
                    holdToken: this.chart ? this.chart.holdToken : "",
                    ...(this.collectionId
                        ? {
                              collectionId: this.collectionId,
                              collectionUrl: this.collectionUrl,
                          }
                        : {}),
                    ...(action ? {action} : {}),
                });
        },

        handleRemindMeClick() {
            this.showRemindMeModal = true;
        },

        closeRemindMeModal() {
            this.showRemindMeModal = false;
        },

        getButtonPriceDescription(deliveryFeeMessage) {
            const bookingClause = this.anyTicketsSelected && this.totalBookingFees > 0
                ? `Price includes a $${this.totalBookingFees.toFixed(2)} booking fee.`
                : `Price includes booking fees.`;

            const additionalFees = [this.totalTransactionFeeMessage, deliveryFeeMessage].filter(Boolean);
            if (additionalFees.length === 0) return bookingClause;

            const additional = additionalFees.join(" and ");
            const verb = additionalFees.length > 1 ? "apply" : "applies";
            return `${bookingClause} ${additional.charAt(0).toUpperCase()}${additional.slice(1)} ${verb} per order.`;
        },

        updateAutoScanCallback(newValue) {
            this.autoScanPreference = newValue;
        },

        getHighestTicketPrice() {
            if (!this.selectedTickets || this.selectedTickets.length === 0) return 0;
            return Math.max(...this.selectedTickets.map((ticket) => ticket.price));
        },

        getHighestTicketPriceBase() {
            if (!this.selectedTickets || this.selectedTickets.length === 0) return 0;
            return Math.max(...this.selectedTickets.map((ticket) => ticket.priceBase));
        },

        getCheapestTicketPrice() {
            const availableTickets = this.inventory
                ? Object.values(this.inventory).filter((ticket) => ticket.isOnSale && ticket.isAvailable)
                : [];
            if (!availableTickets || availableTickets.length === 0) return 0;
            return Math.min(...availableTickets.map((ticket) => ticket.price));
        },

        getCheapestTicketPriceBase() {
            const availableTickets = this.inventory
                ? Object.values(this.inventory).filter((ticket) => ticket.isOnSale && ticket.isAvailable)
                : [];
            if (!availableTickets || availableTickets.length === 0) return 0;
            return Math.min(...availableTickets.map((ticket) => Big(ticket.price).minus(ticket.bookingFee).toNumber()));
        },

        getLowestPossibleTransactionFee() {
            if (!this.charges || this.charges.length === 0) return 0;

            const availableTickets = this.inventory
                ? Object.values(this.inventory).filter((ticket) => ticket.isOnSale && ticket.isAvailable)
                : [];

            if (!availableTickets || availableTickets.length === 0) return 0;

            const cheapestTicketPrice = this.getCheapestTicketPrice();
            const cheapestTicketPriceBase = this.getCheapestTicketPriceBase();

            const calculateFeeForAmount = (charge, baseAmount) => {
                if (!charge.chargeBrackets || charge.chargeBrackets.length === 0) return 0;

                const baseAmountBig = Big(baseAmount);
                const applicableBracket = charge.chargeBrackets
                    ?.sort((a, b) => b.minimumAmount - a.minimumAmount)
                    ?.find((bracket) => baseAmountBig.gte(bracket.minimumAmount));

                if (!applicableBracket) return 0;

                const chargeAmount =
                    charge.chargeType === chargeType.percentage
                        ? baseAmount <= 0
                            ? 0
                            : Big(applicableBracket.amount).div(100).times(baseAmount).toNumber()
                        : applicableBracket.amount;

                return chargeAmount;
            };

            const calculateTotalFeeForTicket = (ticketPrice, ticketPriceBase) => {
                return this.charges.reduce((total, charge) => {
                    const baseAmount =
                        charge.calculationMethod === chargeCalculationMethod.highestTicketPrice ? ticketPriceBase : ticketPrice;
                    return total + calculateFeeForAmount(charge, baseAmount);
                }, 0);
            };

            const feeForCheapest = calculateTotalFeeForTicket(cheapestTicketPrice, cheapestTicketPriceBase);

            // If the fee is $0, find the next tier that has a non-zero fee
            if (feeForCheapest === 0) {
                // Get all available ticket prices above the cheapest
                const availableTicketPrices = availableTickets
                    .map((ticket) => ({
                        price: ticket.price,
                        priceBase: Big(ticket.price).minus(ticket.bookingFee).toNumber(),
                    }))
                    .filter((ticket) => ticket.price > cheapestTicketPrice)
                    .sort((a, b) => a.price - b.price);

                // Only look for higher tiers if there are actually other ticket prices available
                if (availableTicketPrices.length > 0) {
                    // Try each available ticket price to see if it results in a non-zero fee
                    for (const ticket of availableTicketPrices) {
                        const testFee = calculateTotalFeeForTicket(ticket.price, ticket.priceBase);
                        if (testFee > 0) {
                            return testFee;
                        }
                    }

                    // If no available ticket prices result in fees, check charge bracket minimums
                    // but only for amounts that correspond to actual available ticket prices
                    const allMinimumAmounts = this.charges
                        .flatMap((charge) => charge.chargeBrackets?.map((b) => b.minimumAmount) || [])
                        .filter((amount) => amount > cheapestTicketPrice)
                        .sort((a, b) => a - b);

                    // Try each bracket tier in ascending order until we find one with a non-zero fee
                    for (const testAmount of allMinimumAmounts) {
                        // For bracket minimums, use the same value for both price and priceBase
                        // as we don't have context of what the booking fee would be
                        const testFee = calculateTotalFeeForTicket(testAmount, testAmount);
                        if (testFee > 0) {
                            return testFee;
                        }
                    }
                }

                // If there are no other ticket prices available, or all fees are $0, return 0
                return 0;
            }

            return feeForCheapest;
        },

        currencyFormat,
    },
    directives: {
        addCurrentQueryStringToLinks: {
            mounted(el) {
                addCurrentQueryStringToLinks(el);
            },
        },
    },
});

if (isV2) {
    vm.component("ph-arrow-down", PhArrowDown);
    vm.component("ph-arrow-left", PhArrowLeft);
    vm.component("ph-arrow-right", PhArrowRight);
    vm.component("ph-arrow-up-right", PhArrowUpRight);
    vm.component("ph-bell-ringing", PhBellRinging);
    vm.component("ph-calendar-dots", PhCalendarDots);
    vm.component("ph-calendar-plus", PhCalendarPlus);
    vm.component("ph-calendar-star", PhCalendarStar);
    vm.component("ph-caret-down", PhCaretDown);
    vm.component("ph-check", PhCheck);
    vm.component("ph-circle-notch", PhCircleNotch);
    vm.component("ph-clock", PhClock);
    vm.component("ph-envelope", PhEnvelope);
    vm.component("ph-envelope-simple", PhEnvelopeSimple);
    vm.component("ph-facebook-logo", PhFacebookLogo);
    vm.component("ph-hourglass", PhHourglass);
    vm.component("ph-identification-card", PhIdentificationCard);
    vm.component("ph-key", PhKey);
    vm.component("ph-link", PhLink);
    vm.component("ph-lock", PhLock);
    vm.component("ph-map-pin", PhMapPin);
    vm.component("ph-share-network", PhShareNetwork);
    vm.component("ph-shopping-bag", PhShoppingBag);
    vm.component("ph-ticket", PhTicket);
    vm.component("ph-warning-circle", PhWarningCircle);
    vm.component("ph-whatsapp-logo", PhWhatsappLogo);
    vm.component("ph-x-logo", PhXLogo);
}

vm.mount("#events-page");
